import styled from 'styled-components'
import border from '../../tokens/border'
import colors from '../../tokens/colors'
import fonts from '../../tokens/fonts'
import layout from '../../tokens/layout'
import { defaultTheme } from '../../theming/data/defaultTheme'
import { composeTheme } from '../../utils/compose-theme'

type WrapperProps = {
  animation?: boolean
  largest?: boolean
}

export const TableContainer = styled.table.attrs((props) =>
  composeTheme(props),
)`
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;

  caption,
  tbody,
  tfoot,
  thead,
  tr,
  th,
  td {
    border-collapse: collapse;
    font-family: ${fonts.inter};
    vertical-align: middle;
    font-size: ${(props) => props.theme.table.fontSize};
    font-style: normal;
    font-weight: ${(props) => props.theme.table.fontWeight};
    line-height: ${(props) => props.theme.table.fontHeight};
    letter-spacing: 0;
    color: ${(props) => props.theme.table.color};
  }
`

export const TableBody = styled.tbody``

export const ColumnContainer = styled.div`
  display: flex;
  cursor: pointer;
  margin-right: ${layout.margin.spacingXxs};
  justify-content: space-between;

  > svg {
    transition: all 0.4s ease;
    transform: rotateZ(0deg);
    justify-content: flex-end;
  }
`

export const TableCell = styled.td`
  &:first-child {
    padding-left: ${layout.padding.spacingInsetSm};
  }

  ${ColumnContainer} svg {
    transition: all 0.4s ease;
    transform: rotateZ(0deg);
  }

  &.is-ordened {
    ${ColumnContainer} svg {
      transform: rotateZ(180deg);
    }
  }
`

export const TableHead = styled.thead.attrs((props) => composeTheme(props))`
  > tr {
    height: ${(props) => props.theme.table.headerHeight};
    background-color: ${(props) => props.theme.table.headerBackground};
    border-color: ${(props) => props.theme.table.headerBorderColor};
    text-transform: ${(props) => props.theme.table.headerTextTransform};

    > td {
      font-size: ${(props) => props.theme.table.headerFontSize};
      font-style: normal;
      font-weight: ${(props) => props.theme.table.headerFontWeight};
      line-height: ${(props) => props.theme.table.headerFontHeight};
      letter-spacing: 0;
      text-align: left;
      color: ${(props) => props.theme.table.headerColor};
    }
  }
`

export const IconContainer = styled.div`
  display: flex;
  justify-content: end;
  margin-right: ${layout.margin.spacingXxs};
`

export const TableRow = styled.tr.attrs((props) => composeTheme(props))`
  height: ${(props) => props.theme.table.rowHeight};
  background-color: ${(props) => props.theme.table.rowBackground};
  border: ${(props) => props.theme.table.rowBorderWidth} solid
    ${(props) => props.theme.table.rowBorderColor};
  border-top-color: ${(props) => props.theme.table.rowBorderTopColor};
  border-bottom: none;

  ${IconContainer} svg {
    transition: all 0.4s ease;
    transform: rotateZ(0deg);
  }

  &:hover {
    background-color: ${(props) => props.theme.table.rowHoverBackground};
  }

  &.collapsable {
    cursor: pointer;
  }
  &.disabled td {
    color: ${(props) => props.theme.table.disabledColor};

    &:hover {
      background-color: none !important;
    }
  }

  &.is-open {
    background: ${(props) => props.theme.brandColors.primaryLight};
    border-color: ${(props) => props.theme.brandColors.primaryPure};
    border: 0.1rem solid ${(props) => props.theme.brandColors.primaryPure} !important;

    ${IconContainer} svg {
      transform: rotateZ(180deg);
    }
  }

  &.light {
    background: ${colors.neutralColorHighPure};
    border-width: 0.1rem 0;
    border-style: solid;
    border-color: ${colors.neutralColorLowLight};
  }
`

export const CollapsedRow = styled.tr<{ isOpen: boolean }>`
  box-shadow: 0 0.8rem 1.6rem rgba(0, 0, 0, 0.08);

  > td {
    padding: ${(props) => (props.isOpen ? '2.4rem' : '0')};
    background-color: ${colors.neutralColorHighPure};
    transition: 0.15s all ease;
  }
`

export const SpacingRow = styled.tr<{ isOpen: boolean }>`
  height: ${(props) => (props.isOpen ? '2.5rem' : '0')};
  display: ${(props) => (props.isOpen ? 'table-row' : 'none')};
  transition: 0.15s all ease;
`

export const BoxShadow = styled.div.attrs((props) => composeTheme(props))`
  position: absolute;
  width: 1.6rem;
  height: 100%;
  background: ${(props) =>
    props.theme.table?.shadow ||
    `linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(0, 0, 0, 0.08) 100%
  )`};
  pointer-events: none;
  z-index: 1;
  left: 98.6%;
`

export const ContainerAnimation = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  background: rgba(0, 0, 0, 0.16);
  animation: container-animation 4s 1;
  @keyframes container-animation {
    0% {
      opacity: 1;
      z-index: 1;
    }
    80% {
      opacity: 1;
      z-index: 1;
    }
    100% {
      opacity: 0;
      z-index: 0;
    }
  }
`

export const DisplayAnimation = styled.div`
  position: relative;
  height: 100%;
  pointer-events: none;

  svg:nth-child(1) {
    position: absolute;
    top: 50%;
    opacity: 0;
    animation: svg-hand 4s 1;
    @keyframes svg-hand {
      0% {
        opacity: 0;
      }
      10% {
        opacity: 0;
      }
      20% {
        opacity: 1;
      }
      30% {
        right: 45%;
      }
      60% {
        right: 55%;
      }
      70% {
        opacity: 1;
      }
      100% {
        opacity: 0;
        right: 55%;
      }
    }
  }

  svg:nth-child(2) {
    position: absolute;
    top: 51%;
    opacity: 0;
    animation: svg-arrow 4s 1;
    @keyframes svg-arrow {
      0% {
        opacity: 0;
      }
      10% {
        opacity: 0;
      }
      20% {
        opacity: 1;
      }
      30% {
        right: calc(45% - 0.3rem);
      }
      60% {
        right: calc(55% - 0.3rem);
      }
      70% {
        opacity: 1;
      }
      100% {
        opacity: 0;
        right: calc(55% - 0.3rem);
      }
    }
  }
`

export const Wrapper = styled.div.attrs((props) =>
  composeTheme(props),
)<WrapperProps>`
  border-radius: ${(props) => props.theme.table.borderRadius};
  border-width: ${(props) => props.theme.table.borderWidth};
  border-color: ${(props) => props.theme.table.borderColor};
  border-style: ${(props) => props.theme.table.borderStyle};
  overflow-x: ${(props) => (props.largest ? 'auto' : 'inherit')};
  position: relative;
  scrollbar-width: auto;
  scrollbar-color: ${colors.neutralColorLowLight}
    ${colors.neutralColorHighMedium};
  ::-webkit-scrollbar {
    height: 0.8rem;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
    border: ${border.width.widthHairline} solid ${colors.neutralColorLowLight};
  }
  ::-webkit-scrollbar-thumb {
    background: ${colors.neutralColorLowMedium};
  }

  ${(props) =>
    props.animation &&
    `
    animation: wrapper-animation 4s 1;
    @keyframes wrapper-animation {
      0% { background-color: rgba(0, 0, 0, 0.16); }
      80% { background-color: rgba(0, 0, 0, 0.16); }
      100% { background-color: transparent; }
    }
  `}
`

export const Container = styled.div`
  position: relative;
`

TableContainer.defaultProps = defaultTheme
TableHead.defaultProps = defaultTheme
TableRow.defaultProps = defaultTheme
